import { Button } from '@components/atoms/Button/Button';
import { LoadingIndicator } from '@components/atoms/LoadingIndicator/LoadingIndicator';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { Title } from '@components/atoms/Title';
import { Person } from '@components/molecules/Person/Person';
import { Default as DefaultLayout } from '@components/templates/layouts/Default';
import { GetPersonsDocument } from '@lib/graphql/generated';
import { extractSsrState } from '@lib/graphql/helpers/extractSsrState';
import { useTagManager } from '@lib/hooks/useTagManager';
import { createUrqlClient, usePaginatedQuery } from '@src/lib/graphql/urql';
import { getCookie } from '@utils/cookies';
import { AditionInitialization, AditionTagPlacement } from '@vgn-medien-holding/vgn-fe-components';
import Head from 'next/head';
import React from 'react';

// Stars
const ad_settings_Stars = {
  mobile_middle1: {
    id: 5256646,
    name: 'TVM_Mobile_Stars_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

const PERSONS_PER_PAGE = 24;

export function Persons() {
  const [{ fetching: personsLoading, data: personsData, hasMorePages, fetchNextPage }] = usePaginatedQuery({
    query: GetPersonsDocument,
    variables: { search: '', page: 1, first: PERSONS_PER_PAGE },
  });
  const persons = personsData?.flatMap((data) => data?.persons?.data);

  useTagManager({
    event: 'metadata',
    loginStatus: 'nicht eingeloggt',
    seitentyp: 'Personen',
    titel: 'Personen',
    oewaSeitenkontingent: 'RedCont',
    oewaSubkontingent: 'KulturUndFreizeit',
    oewaProfilingkontingent: 'KulturUeberblick',
    cookieAlert: getCookie('OptanonConsent'),
  });

  return (
    <DefaultLayout>
      <AditionInitialization data={{ ad_settings: ad_settings_Stars }} />
      <Oewa pageKey={'RedCont'} categoryKey={'KulturUndFreizeit'} profileKey={'KulturUeberblick'} />
      <Head>
        <title>Stars | TV-MEDIA</title>
        {Meta(
          {
            meta_title: 'Stars | TV-MEDIA',
            meta_description: 'Infos zu Stars und Schauspieler:innen aus Filmen, Serien und Shows.',
          },
          'Stars | TV-MEDIA',
        )}
      </Head>
      <div>
        <Title classProps={{ heading: 'text-xl lg:text-xl mb-6' }} level={1}>
          Stars
        </Title>
        <LoadingIndicator visible={persons?.length < 1 && personsLoading} />
        <div className="mt-6 grid grid-cols-fill-actors justify-items-stretch gap-4">
          {persons?.map((person, index) => (
            <React.Fragment key={person.id}>
              <Person person={person} classProps={{ root: 'w-full' }} />
              {index % 3 === 2 && (
                <AditionTagPlacement
                  tag={ad_settings_Stars.mobile_middle1}
                  classProps={{ root: 'w-full lg:hidden grid place-items-center' }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        {hasMorePages && (
          <div className="text-center">
            <Button
              title="Mehr anzeigen"
              hasAction
              isLoading={personsLoading}
              onAction={() => setTimeout(fetchNextPage)}
              buttonStyle="secondary"
              classProps={{ root: 'mt-6' }}
            />
          </div>
        )}
      </div>
    </DefaultLayout>
  );
}

export async function personenSsrLogic() {
  const { ssrQuery, ssrCache } = createUrqlClient();
  await ssrQuery({
    query: GetPersonsDocument,
    variables: { search: '', page: 1, first: PERSONS_PER_PAGE },
  });

  return {
    props: {
      slug: 'index',
      urqlState: extractSsrState(ssrCache),
    },
    revalidate: 30,
  };
}
